import { MerchantFileUploadDocType } from 'APIs/files/files.types';
import { MerchantPerson } from 'APIs/merchant.persons/merchant.persons.types';
import FileViewer from 'Components/Common/FileViewer';
import { ReactElement, useCallback, useState } from 'react';

import PersonDataTable from './PersonDataTable';
import PersonModal from './PersonModal';
import { AddNewPerson, TableHeader } from './styles';
import { PersonFormProps, toPersonFormProps } from './types';

type Props = {
  actionPersons: Array<string>;
  persons: Array<MerchantPerson>;
  fetchingError: string;
  merchantPerson: MerchantPerson | null;
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  isModalLoading: boolean;
  isLoading: boolean;
  modalErrorMsg: string;
  setMerchantPerson: (merchantPerson: MerchantPerson | null) => void;
  savePersonHandler: (formData: PersonFormProps) => void;
  handleFileUploads: (file: File, docType: MerchantFileUploadDocType) => Promise<string | null>;
  deletePersonHandler: (ownerId: string) => Promise<void>;
  downloadFile: (fileId: string) => Promise<{ fileUrl: string; fileType: string } | null>;
};

const PersonList = ({
  actionPersons,
  persons,
  fetchingError,
  merchantPerson,
  showModal,
  setShowModal,
  isModalLoading,
  isLoading,
  modalErrorMsg,
  setMerchantPerson,
  savePersonHandler,
  handleFileUploads,
  deletePersonHandler,
  downloadFile,
}: Props): ReactElement => {
  const [displayFileId, setDisplayFileId] = useState<string | null>(null);

  const getPerson = useCallback(
    (ownerId: string): MerchantPerson | null => {
      if (persons.length === 0) return null;
      const personToEdit = persons.filter((person) => person.ownerId === ownerId);

      if (personToEdit.length === 0) return null;
      return personToEdit[0];
    },
    [persons],
  );

  const handleAddNew = () => {
    setMerchantPerson(null);
    setShowModal(true);
  };

  const editPerson = useCallback(
    (ownerId: string) => {
      const mPerson = getPerson(ownerId);
      if (mPerson) {
        setMerchantPerson(mPerson);
        setShowModal(true);
      }
    },
    [getPerson, setMerchantPerson, setShowModal],
  );

  const handleFileClick = (e: React.MouseEvent<HTMLButtonElement>, fileId: string) => {
    e.preventDefault();
    setDisplayFileId(fileId);
  };

  const handleFileModalClose = () => {
    setDisplayFileId(null);
  };

  return (
    <div>
      <TableHeader>
        <h3 className="mb-0">Beneficial owners</h3>
        <AddNewPerson onClick={handleAddNew}>Add new</AddNewPerson>
      </TableHeader>

      <PersonDataTable
        personList={persons}
        fetchListLoader={isLoading}
        isErrorRequest={!!fetchingError}
        actionPersons={actionPersons}
        handleEditPerson={editPerson}
        handleDeletePerson={deletePersonHandler}
        handleFileClick={handleFileClick}
      />

      {showModal && (
        <PersonModal
          personFormProps={merchantPerson ? toPersonFormProps(merchantPerson) : null}
          savePersonHendler={savePersonHandler}
          modalErrorMsg={modalErrorMsg}
          isModalLoading={isModalLoading}
          setShowModal={setShowModal}
          handleFileUploads={handleFileUploads}
        />
      )}
      <FileViewer
        displayFileId={displayFileId}
        downloadFile={downloadFile}
        handleFileModalClose={handleFileModalClose}
      />
    </div>
  );
};

export default PersonList;
